import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Shared Components
import SectionTitle from './shared/SectionTitle';
import ProductCard from './shared/ProductCard';
import LoadingSkeleton from './shared/LoadingSkeleton';

const ProductsGrid = ({ section, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visibleItems, setVisibleItems] = useState(new Set());
  const observerRef = useRef(null);

  const items = useMemo(() => {
    return section?.items || [];
  }, [section?.items]);
  const columns = isMobile ? 1 : (section?.config?.columns || 3);

  const handleProductClick = (product) => {
    if (product.link_url) {
      navigate(product.link_url);
    }
  };

  const handleWishlist = (productId) => {
    console.log('Adding to wishlist:', productId);
    // TODO: Implement wishlist functionality
  };

  const handleCompare = (productId) => {
    console.log('Adding to compare:', productId);
    // TODO: Implement compare functionality
  };

  // Intersection Observer for staggered animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleItems((prev) => new Set([...prev, entry.target.dataset.index]));
            }, parseInt(entry.target.dataset.delay) || 0);
            // Once visible, stop observing
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [items]);

  if (isLoading) {
    return (
      <LoadingSkeleton
        type="card"
        count={columns * 2}
        columns={columns}
        height={300}
      />
    );
  }

  if (!items.length) return null;

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="lg">
        {/* Section Title with Animation */}
        <SectionTitle
          title={section.title}
          align="center"
        />

        {/* Products Grid with Staggered Animation */}
        <Grid container spacing={3}>
          {items.map((item, index) => (
            <Grid
              item
              xs={12 / columns}
              key={item.id}
              data-animate
              data-index={index}
              data-delay={index * 100}
              sx={{
                opacity: visibleItems.has(index.toString()) ? 1 : 0,
                transform: visibleItems.has(index.toString())
                  ? 'translateY(0) scale(1)'
                  : 'translateY(30px) scale(0.9)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transformOrigin: 'center bottom',
              }}
            >
              <ProductCard
                product={item}
                onClick={handleProductClick}
                onWishlist={handleWishlist}
                onCompare={handleCompare}
                showActions={true}
                showBadge={true}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductsGrid;
